import { AxiosError } from 'axios';
import { types, flow, Instance } from 'mobx-state-tree';
import axios from 'utils/axios.config';
import { getQueryString } from 'utils/prepareData';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { ProductBasicFormData } from 'components/Forms/ProductBasicForm/types';
import { withBrandStore } from 'stores/brands/brands.store';
import { withTableStore } from 'stores/table/table.store';
import { ErrorData, IResponse, StatusTypeReq } from 'types/common';
import {
  ICreateProductFromCatalogueReqData,
  IProductData,
  IProductListData,
  IProductsCategoryData,
  IQueryProductsData,
} from './types';
import { DEFAULT_PRODUCT_QUERY } from './constatnts';

const { model, frozen, maybeNull } = types;

export const ProductsStore = model('ProductsStore', {
  productsQuery: frozen<IQueryProductsData>(),
  productsList: maybeNull(frozen<IProductListData[]>()),
  productsCategories: maybeNull(frozen<IProductsCategoryData[]>()),
  selectedProduct: maybeNull(frozen<IProductData>()),
})
  .extend(withAlertStore)
  .extend(withBrandStore)
  .extend(withTableStore)
  // .views((self) => ({}))
  .actions((self) => ({
    updateProductsQuery(query: IQueryProductsData | null) {
      self.productsQuery = query ? { ...self.productsQuery, ...query } : DEFAULT_PRODUCT_QUERY;
    },
    setProductsList(data: IProductListData[] | null) {
      self.productsList = data;
      // self.tableStore.setStartPageIndex(0);
    },
    addProductsList(data: IProductListData[]) {
      self.productsList = [...(self.productsList || []), ...data];
    },
    setProductsCategories(data: IProductsCategoryData[]) {
      self.productsCategories = data;
    },
    setSelectedProduct(data: IProductData | null) {
      self.selectedProduct = data;
    },
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  .actions((self) => ({
    getProductsCategories: flow(function* () {
      yield axios
        .get(`product/categories`)
        .then(({ data: { data } }) => self.setProductsCategories(data))
        .catch(self.errorHandling);
    }),
    getProductsList: flow(function* (query: IQueryProductsData) {
      if (!query.offset) self.updateProductsQuery(query);
      const { categories, brand, search, createdByData, isDeleted, ...others } = {
        ...self.productsQuery,
        ...query,
      };
      const queryStr = getQueryString({
        ...others,
        categories: JSON.stringify(categories?.map(({ id }) => id)),
        isDeleted: isDeleted?.id,
        brandId: brand?.id,
        search: encodeURIComponent(search || ''),
        createdBy: createdByData?.id,
      });
      yield axios
        .get(`product?${queryStr}`)
        .then(({ data: { data } }) => {
          if (query.offset) self.addProductsList(data);
          else {
            self.setProductsList(data);
            self.tableStore.setStartPageIndex(0);
          }
        })
        .catch(self.errorHandling);
    }),
    getProduct: flow(function* (id: string) {
      yield axios
        .get(`product/${id}`)
        .then(({ data: { data } }) => self.setSelectedProduct(data))
        .catch(self.errorHandling);
    }),
    getAutoCompleteProducts: flow(function* (search: string) {
      const brandId = self.brandsStore.brandBasicData?.id;
      if (brandId) {
        const result = yield axios
          .get(`product/autocomplete/${brandId}?search=${encodeURIComponent(search)}`)
          .then((res) => res.data.data)
          .catch(self.errorHandling);
        return result;
      }
    }),
    addProduct: flow(function* (data: Omit<ProductBasicFormData, 'imageUrlFile'>) {
      const sendData: ICreateProductFromCatalogueReqData = {
        ...data,
        categories: data.categories.map(({ id }) => id),
        brandId: data.brand?.id || '',
      };
      const newBrand = yield axios
        .post(`product`, sendData)
        .then(({ data: { data } }) => {
          self.setSelectedProduct(data);
          return data;
        })
        .catch(self.errorHandling);
      return newBrand;
    }),
    editProduct: flow(function* (data: Omit<ProductBasicFormData, 'imageUrlFile'>) {
      const sendData: ICreateProductFromCatalogueReqData = {
        ...data,
        categories: data.categories.map(({ id }) => id),
        brandId: data.brand?.id || '',
      };
      const newBrand = yield axios
        .patch(`product/${data.id}`, sendData)
        .then(({ data: { data: newData } }) => {
          self.setSelectedProduct(newData);
          if (self.productsList) {
            self.setProductsList(self.productsList.map((item) => (item.id === newData.id ? newData : item)));
          }
          return newData;
        })
        .catch(self.errorHandling);
      return newBrand;
    }),
    deleteProduct: flow(function* (id: string) {
      yield axios
        .delete(`product/${id}`)
        .then(() => {
          if (self.productsList) {
            self.setProductsList(self.productsList.filter((item) => item.id !== id));
          }
        })
        .catch(self.errorHandling);
    }),
    changeProductStatus: flow(function* ({ id, status }: StatusTypeReq) {
      yield axios
        .patch(`product/status/${id}`, { status })
        .then(() => {
          if (self.productsList) {
            self.setProductsList(self.productsList.map((item) => (item.id === id ? { ...item, status } : item)));
          }
        })
        .catch(self.errorHandling);
      return id;
    }),
  }));

export type IProductsStore = Instance<typeof ProductsStore>;
